/**
 * Treasury bill pull-to-par accretion (S5 chart #219 / S8 sell Est. return #223).
 *
 * A held T-Bill's value accretes **straight-line** ("pull to par") from the price paid at
 * acceptance to its face value at maturity. This is a display-side estimate only — the client
 * has no true per-day mark-to-market series (see valueHistorySeries.ts). It is deliberately
 * distinct from {@link ./treasuryBillPricing} which is the PURCHASE-side conservative estimate.
 *
 * All functions inject `asOf` for deterministic tests. A zero or inverted term
 * (maturity <= acceptance) is treated as fully matured: value = faceValue, progress = 1.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface AccretedValueArgs {
  purchasePrice: number; // pricePaid at acceptance
  faceValue: number; // value at maturity
  acceptanceDate: Date; // orderDateTimeUtc (or the 'purchase' transaction date)
  maturityDate: Date;
  asOf?: Date; // default new Date()
}

/**
 * Fraction of the term completed, in days, clamped to [0, 1].
 *
 * A zero or inverted term (maturity <= acceptance) is fully complete → returns 1.
 */
export function termProgress(
  acceptanceDate: Date,
  maturityDate: Date,
  asOf: Date = new Date(),
): number {
  const acceptanceTime = acceptanceDate.getTime();
  const maturityTime = maturityDate.getTime();

  // Zero / inverted term → treat as matured.
  if (maturityTime <= acceptanceTime) {
    return 1;
  }

  const totalDays = (maturityTime - acceptanceTime) / MS_PER_DAY;
  const elapsedDays = (asOf.getTime() - acceptanceTime) / MS_PER_DAY;
  const fraction = elapsedDays / totalDays;

  return Math.max(0, Math.min(1, fraction));
}

/**
 * Linear pull-to-par value at `asOf`, clamped to [purchasePrice, faceValue].
 *
 *   value = purchasePrice + (faceValue - purchasePrice) * termProgress
 *
 * Before acceptance → purchasePrice; after maturity → faceValue; zero/inverted term → faceValue.
 */
export function accretedValue(args: AccretedValueArgs): number {
  const { purchasePrice, faceValue, acceptanceDate, maturityDate, asOf = new Date() } = args;

  const progress = termProgress(acceptanceDate, maturityDate, asOf);
  const value = purchasePrice + (faceValue - purchasePrice) * progress;

  // Clamp to [purchasePrice, faceValue] regardless of ordering (defensive against a
  // purchasePrice above faceValue, e.g. an above-par premium bill).
  const lo = Math.min(purchasePrice, faceValue);
  const hi = Math.max(purchasePrice, faceValue);
  return Math.max(lo, Math.min(hi, value));
}

export interface EstimatedSaleReturnArgs extends AccretedValueArgs {
  /** Nominal (face-value) amount being sold. Proceeds scale by `sellAmount / faceValue`. */
  sellAmount: number;
}

/**
 * Estimated proceeds for selling `sellAmount` nominal of a holding, at `asOf`.
 *
 *   estimatedSaleReturn = accretedValue(full holding) * (sellAmount / faceValue)
 *
 * The full-holding accreted value is scaled by the sold nominal fraction. Returns 0 when the
 * bill is not sellable in a meaningful way: a non-positive `faceValue` or `sellAmount`, or a
 * zero/inverted term. The caller renders '—' from a 0 result.
 */
export function estimatedSaleReturn(args: EstimatedSaleReturnArgs): number {
  const { purchasePrice, faceValue, acceptanceDate, maturityDate, sellAmount, asOf } = args;

  // Guard divide-by-zero and non-sellable inputs.
  if (!(faceValue > 0) || !(sellAmount > 0)) {
    return 0;
  }
  // Zero / inverted term → not sellable via accretion; caller falls back to '—'.
  if (maturityDate.getTime() <= acceptanceDate.getTime()) {
    return 0;
  }

  const fullHoldingValue = accretedValue({
    purchasePrice,
    faceValue,
    acceptanceDate,
    maturityDate,
    asOf,
  });

  return fullHoldingValue * (sellAmount / faceValue);
}
